import { OperationError } from './../model/operation-error.model';
import { WebsiteUserAutoCompleteSearchBackend } from '../service/website-user-auto-compete-search.service';
import { Injectable, inject } from '@angular/core';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import {
  CreateOutOfOfficePeriodFormModel,
  CreateTemporaryApproverRequestModel,
  CrudOperationStatus,
  OutOfOfficePeriod,
  PrimaryApproverOutOfOfficePeriod,
  TemporaryApproverUser,
} from '@rx-applications/clientsite/temporary-approver/utils';
import { TemporaryApproverBackendService } from '../service/temporary-approver-backend.service';
import { Observable, exhaustMap, filter, map, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { LoadStatus, isInThePast } from '@rx-applications/shared/utils';
import { addDays, isFuture, isPast } from 'date-fns';

export interface TemporaryApproverState {
  outOfOfficePeriods: OutOfOfficePeriod[];
  primaryApproverOutOfOfficePeriods: PrimaryApproverOutOfOfficePeriod[];
  temporaryApproverUsers: TemporaryApproverUser[];
  temporaryApproverUsersLoadStatus: LoadStatus;
  crudOperationStatus: CrudOperationStatus;
  error: any;
}

const initialState: TemporaryApproverState = {
  outOfOfficePeriods: null,
  primaryApproverOutOfOfficePeriods: null,
  crudOperationStatus: 'NOT_LOADED',
  error: null,
  temporaryApproverUsers: null,
  temporaryApproverUsersLoadStatus: 'NOT_LOADED',
};

@Injectable()
export class TemporaryApproverStore extends ComponentStore<TemporaryApproverState> {
  #temporaryApproverBackendService: TemporaryApproverBackendService = inject(TemporaryApproverBackendService);
  #websiteUserAutoCompleteSearch: WebsiteUserAutoCompleteSearchBackend = inject(WebsiteUserAutoCompleteSearchBackend);

  constructor() {
    super(initialState);
  }

  readonly selectOutOfOfficePeriods$: Observable<OutOfOfficePeriod[]> = this.select((state) => state.outOfOfficePeriods);
  readonly selectActivePrimaryApproverOutOfOfficePeriods$: Observable<PrimaryApproverOutOfOfficePeriod[]> = this.select(
    (state) => state.primaryApproverOutOfOfficePeriods,
  ).pipe(
    filter((primaryApproverOutOfOfficePeriods) => Boolean(primaryApproverOutOfOfficePeriods)),
    map((primaryApproverOutOfOfficePeriods) =>
      primaryApproverOutOfOfficePeriods.filter(
        (period) =>
          period.isActive && isPast(new Date(period.startDateTimeUtc)) && isFuture(new Date(period.endDateTimeUtc)),
      ),
    ),
  );

  readonly primaryPeriodsExpiringSoon$: Observable<PrimaryApproverOutOfOfficePeriod[]> =
    this.selectActivePrimaryApproverOutOfOfficePeriods$.pipe(
      map((activePrimaryApproverOutOfOfficePeriod) =>
        activePrimaryApproverOutOfOfficePeriod.filter(
          (activePeriod) => new Date(activePeriod.endDateTimeUtc).getTime() <= addDays(new Date(), 1).getTime(),
        ),
      ),
      map((activePrimaryApproverOutOfOfficePeriod) =>
        activePrimaryApproverOutOfOfficePeriod.length
          ? activePrimaryApproverOutOfOfficePeriod.sort(
              (x, y) => new Date(x.endDateTimeUtc).getTime() - new Date(y.endDateTimeUtc).getTime(),
            )
          : null,
      ),
    );

  readonly primaryPeriodsExpiringAfter24h$: Observable<PrimaryApproverOutOfOfficePeriod[]> =
    this.selectActivePrimaryApproverOutOfOfficePeriods$.pipe(
      map((primaryPeriodsExpiringAfter24h) =>
        primaryPeriodsExpiringAfter24h.filter(
          (activePeriod) => new Date(activePeriod.endDateTimeUtc).getTime() > addDays(new Date(), 1).getTime(),
        ),
      ),
      map((primaryPeriodsExpiringAfter24h) =>
        primaryPeriodsExpiringAfter24h.length
          ? primaryPeriodsExpiringAfter24h.sort(
              (x, y) => new Date(x.endDateTimeUtc).getTime() - new Date(y.endDateTimeUtc).getTime(),
            )
          : null,
      ),
    );

  readonly isLoading$: Observable<boolean> = this.select((state) => state.crudOperationStatus === 'LOADING');
  readonly isTemporaryApproverCreated$: Observable<boolean> = this.select(
    (state) => state.crudOperationStatus === 'CREATED',
  );
  readonly isTemporaryApproverDeleted$: Observable<boolean> = this.select(
    (state) => state.crudOperationStatus === 'DELETED',
  );
  readonly isLoadingtemporaryApproverUsers$: Observable<boolean> = this.select(
    (state) => state.temporaryApproverUsersLoadStatus === 'LOADING',
  );
  readonly selectTemporaryApproverUsers$: Observable<TemporaryApproverUser[]> = this.select(
    (state) => state.temporaryApproverUsers,
  );
  readonly selectError$: Observable<OperationError> = this.select((state) => state.error);

  readonly getOutOfOfficePeriods = this.effect((trigger$) =>
    trigger$.pipe(
      tap(() => this.patchState({ crudOperationStatus: 'LOADING', error: null })),
      exhaustMap(() =>
        this.#temporaryApproverBackendService.getOutOfOfficePeriods$().pipe(
          tapResponse(
            (outOfOfficePeriods: OutOfOfficePeriod[]) =>
              this.patchState({ outOfOfficePeriods, crudOperationStatus: 'LOADED' }),
            () => this.onError('getOutOfOfficePeriodsError', 'LOADED'),
          ),
        ),
      ),
    ),
  );

  readonly gePrimaryApproverOutOfOfficePeriods = this.effect((trigger$) =>
    trigger$.pipe(
      exhaustMap(() =>
        this.#temporaryApproverBackendService.gePrimaryApproverOutOfOfficePeriods$().pipe(
          tapResponse(
            (primaryApproverOutOfOfficePeriods: PrimaryApproverOutOfOfficePeriod[]) =>
              this.patchState({ primaryApproverOutOfOfficePeriods }),
            () => this.onError('getOutOfOfficePeriodsError', 'LOADED'),
          ),
        ),
      ),
    ),
  );

  readonly deleteOutOfOfficePeriods = this.effect<OutOfOfficePeriod>((outOfOfficePeriod$: Observable<OutOfOfficePeriod>) =>
    outOfOfficePeriod$.pipe(
      tap(() => this.patchState({ crudOperationStatus: 'LOADING', error: null })),
      switchMap((outOfOfficePeriod) =>
        this.#temporaryApproverBackendService.deleteOutOfOfficePeriods$(outOfOfficePeriod.id).pipe(
          tapResponse(
            () => {
              this.patchState({ crudOperationStatus: 'DELETED' });
              this.getOutOfOfficePeriods();
            },
            () => this.onError('deleteOutOfOfficePeriodError', 'DELETED'),
          ),
        ),
      ),
    ),
  );

  readonly createOutOfOfficePeriods = this.effect<CreateOutOfOfficePeriodFormModel>(
    (newOutOfOfficePeriodFormModel$: Observable<CreateOutOfOfficePeriodFormModel>) =>
      newOutOfOfficePeriodFormModel$.pipe(
        tap(() => this.patchState({ crudOperationStatus: 'LOADING', error: null })),
        map((newOutOfOfficePeriodFormModel) =>
          this.mapToCreateOutOfOfficePeriodsRequestModel(newOutOfOfficePeriodFormModel),
        ),
        switchMap((newOutOfOfficePeriod) =>
          this.#temporaryApproverBackendService.createOutOfOfficePeriods$(newOutOfOfficePeriod).pipe(
            tapResponse(
              () => {
                this.patchState({ crudOperationStatus: 'CREATED' });
                this.getOutOfOfficePeriods();
              },
              () => this.onError('createOutOfOfficePeriodsError', 'CREATED'),
            ),
          ),
        ),
      ),
  );

  readonly searchTemporaryApproverUsers = this.effect<string>((query$: Observable<string>) =>
    query$.pipe(
      tap(() => this.patchState({ temporaryApproverUsersLoadStatus: 'LOADING' })),
      switchMap((query) =>
        this.#websiteUserAutoCompleteSearch.searchWebsiteUserAutoComplete$(query).pipe(
          tapResponse(
            (temporaryApproverUsers: TemporaryApproverUser[]) => {
              this.patchState({ temporaryApproverUsersLoadStatus: 'LOADED', temporaryApproverUsers });
            },
            (error: HttpErrorResponse) =>
              this.patchState({ temporaryApproverUsersLoadStatus: 'ERROR', temporaryApproverUsers: null }),
          ),
        ),
      ),
    ),
  );

  clearError(): void {
    this.patchState({ error: null });
  }

  private onError(message: string, operation: CrudOperationStatus): void {
    this.patchState({ error: { message, operation }, crudOperationStatus: 'ERROR' });
  }

  private mapToCreateOutOfOfficePeriodsRequestModel(
    createOutOfOfficePeriodFormModel: CreateOutOfOfficePeriodFormModel,
  ): CreateTemporaryApproverRequestModel {
    const temporaryApproverUser = createOutOfOfficePeriodFormModel.temporaryApproverUser as TemporaryApproverUser;
    const requestModel: CreateTemporaryApproverRequestModel = {
      userId: temporaryApproverUser.userId,
      userName: temporaryApproverUser.userName,
      fullName: temporaryApproverUser.fullName,
      email: temporaryApproverUser.email,
      endDateTimeUtc: new Date(
        `${createOutOfOfficePeriodFormModel.endDateUserTimeZone}T${createOutOfOfficePeriodFormModel.endTimeUserTimeZone}`,
      ).toISOString(),
      startDateTimeUtc: new Date(
        `${createOutOfOfficePeriodFormModel.startDateUserTimeZone}T${createOutOfOfficePeriodFormModel.startTimeUserTimeZone}`,
      ).toISOString(),
    };

    return requestModel;
  }
}
